import { Component } from '@theme/component';

/**
 * Video for a masonry tile that defers loading until the tile is actually
 * visible. Tiles on not-yet-viewed slides sit offscreen in the slideshow's
 * horizontal scroller, so their videos cost nothing until the visitor
 * navigates to that slide. The poster image (rendered in Liquid) shows
 * until then.
 *
 * Reduced-motion users (and browsers that block autoplay) get the poster
 * with the play button in its "play" state — nothing autoplays, but the
 * video stays one click away and only downloads if they ask for it.
 *
 * @typedef {Object} MasonryTileVideoRefs
 * @property {HTMLVideoElement} videoElement - The video element.
 * @property {HTMLElement[]} [videoSources] - The deferred source elements.
 */

/**
 * @extends {Component<MasonryTileVideoRefs>}
 */
class MasonryTileVideo extends Component {
  requiredRefs = ['videoElement'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  #loaded = false;

  connectedCallback() {
    super.connectedCallback();

    const { videoElement } = this.refs;
    videoElement?.addEventListener('play', this.#syncPlaybackState);
    videoElement?.addEventListener('pause', this.#syncPlaybackState);
    this.#syncPlaybackState();

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Start fetching slightly before the tile scrolls into view so the
    // video is ready as the slide lands.
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#load();
        }
      },
      { rootMargin: '200px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = null;
    const { videoElement } = this.refs;
    videoElement?.removeEventListener('play', this.#syncPlaybackState);
    videoElement?.removeEventListener('pause', this.#syncPlaybackState);
  }

  /**
   * Pause/play toggle for the tile's video (WCAG 2.2.2 — auto-playing
   * content needs a pause mechanism). Wired to the button via on:click.
   */
  togglePlayback = () => {
    const { videoElement } = this.refs;
    if (!videoElement) return;

    if (videoElement.paused) {
      this.#load();
      videoElement.play?.()?.catch(() => {});
    } else {
      videoElement.pause();
    }
  };

  /**
   * Mirrors the video's actual playback state onto the component (for the
   * icon swap) and the button's label.
   */
  #syncPlaybackState = () => {
    const { videoElement, playbackButton } = this.refs;
    if (!videoElement) return;

    const paused = videoElement.paused;
    this.toggleAttribute('video-paused', paused);

    if (playbackButton) {
      const label = paused ? playbackButton.dataset.labelPlay : playbackButton.dataset.labelPause;
      if (label) playbackButton.setAttribute('aria-label', label);
    }
  };

  #load() {
    if (this.#loaded) return;
    this.#loaded = true;

    this.#observer?.disconnect();
    this.#observer = null;

    const { videoElement, videoSources = [] } = this.refs;

    const deferredSources = videoSources.filter((source) => source.dataset.videoSource);
    // No deferred sources means they were rendered inline (theme editor) —
    // the video plays natively and reloading it would only restart it.
    if (deferredSources.length === 0) return;

    for (const source of deferredSources) {
      source.setAttribute('src', source.dataset.videoSource);
    }

    videoElement.load();
    // Autoplay usually kicks in on its own; Safari sometimes needs a nudge
    // when the element was attached before its sources.
    videoElement.play?.()?.catch(() => {});
  }
}

if (!customElements.get('masonry-tile-video')) {
  customElements.define('masonry-tile-video', MasonryTileVideo);
}
